import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { LineChart } from "@mui/x-charts/LineChart";

import * as css from "styles/CssVariables.js";
import ButtonPrimarySmall from "components/buttons/ButtonPrimarySmall";

import emailsIcon from "assets/img/icons/icons8-group-message.svg";
import userCircleIcon from "assets/img/icons/user-single-icon.svg";
import userCircleWhiteIcon from "assets/img/icons/user-single-icon-white.svg";
import dotIndigoIcon from "assets/img/icons/icons8-dot-indigo.svg";
import dotYellowIcon from "assets/img/icons/icons8-dot-yellow.svg";
import percentWhiteIcon from "assets/img/icons/percent-icon-white.svg";
import Tip from "components/other/Tip";

const ListBuilding = styled.div`
  position: relative;
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const BoxTitle = styled.div`
  width: 100%;

  line-height: 130%;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
`;

const TitleIcon = styled.div`
  display: inline-block;
  padding-right: 6px;

  transform: translateY(4.5px);

  width: 28px;
  min-width: 28px;
`;

const Text = styled.div`
  line-height: 130%;
`;

const LeadsStats = styled.div`
  width: 100%;

  display: flex;
  gap: 20px;

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const Stats = styled.div`
  max-width: 282px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};

  flex: 0.5;
`;

const SmallTitleWithTip = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const SmallTitle = styled.div`
  font-size: 16px;
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};

  display: flex;
  flex-direction: column;
`;

const StatsBox = styled.div`
  padding: 15px 20px;
  width: 100%;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  flex: 0.5;

  display: grid;
  grid-template-columns: 37px 73px 73px 1fr;
  grid-template-rows: 25px 1fr 1fr 1fr;
`;

const BoxLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const LabelIconCell = styled.div`
  display: flex;
  align-items: center;
`;

const LabelIcon = styled.div`
  width: 16px;
  min-width: 16px;
  height: 16px;
`;

const ValueCell = styled.div`
  height: 24px;

  font-size: 13px;

  display: flex;
  align-items: center;
`;

const ChangeBadge = styled.div`
  height: 16px;
  padding: 0 4px 1px;
  margin-left: 5px;

  font-size: 11px;

  border-radius: 8px;
  background: ${(props) =>
    props.green ? css.POSITIVE_GREEN : css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const Trends = styled.div`
  max-width: 282px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};

  flex: 0.5;
`;

const TrendsBox = styled.div`
  padding: ${css.BOX_PADDING_TERTIARY};
  width: 100%;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  flex: 0.5;
`;

const TrendsHeader = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TrendsLegend = styled.div`
  padding-top: 3px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const TrendsLegendItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

const TrendsLegendItemIcon = styled.div`
  width: 10px;
  min-width: 10px;
  height: 10px;

  display: flex;
`;

const TrendsLegendItemName = styled.div`
  font-size: 13px;
`;

const EmailLists = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const EmailListsBox = styled.div`
  padding: 15px 20px;
  width: 100%;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
`;

const TableWrap = styled.div`
  width: 100%;
  padding-bottom: 5px;
  margin-bottom: -5px;

  overflow-x: auto;
`;

const ListTable = styled.table`
  width: 100%;
  min-width: 560px;
  max-width: 700px;

  border-spacing: 4px;
`;

const TableHead = styled.thead``;

const TableLabel = styled.th`
  padding-bottom: 12px;
  max-width: ${(props) => (props.width ? props.width : "auto")};

  font-size: 18px;
  font-weight: 400;
  text-align: left;
`;

const TableLabelIcon = styled.div`
  width: 20px;
  min-width: 20px;
  height: 20px;
`;

const TableBody = styled.tbody``;

const Cell = styled.td``;

const ButtonWrap = styled.div`
  display: flex;
`;

const LandingPages = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const LabelWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LabelWithIconIcon = styled.div`
  width: 20px;
  min-width: 20px;
  height: 20px;
`;

const LabelWithIconText = styled.div``;

const Autoresponders = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

export default ({ data }) => {
  const navigate = useNavigate();

  const {
    last24HoursLeads,
    last24HoursVerifiedLeads,
    prev24HoursLeads,
    prev24HoursVerifiedLeads,
    last7DaysLeads,
    last7DaysVerifiedLeads,
    prev7DaysLeads,
    prev7DaysVerifiedLeads,
    allLeads,
    allVerifiedLeads,
    trendsData,
  } = data.listBuildingData.leadsStats;

  const changeBadge = (change) => {
    if (typeof change !== "number" || change === 0) return <></>;

    if (change > 100) return <ChangeBadge green={true}>&gt;100%</ChangeBadge>;

    const roundCoef = change >= 10 || change <= -10 ? 1 : 10;
    let changeRounded = Math.round(change * roundCoef) / roundCoef;

    if (changeRounded >= 0)
      return <ChangeBadge green={true}>+{changeRounded}%</ChangeBadge>;
    else return <ChangeBadge green={false}>{changeRounded}%</ChangeBadge>;
  };

  const last24HoursLeadsChange =
    prev24HoursLeads > 0
      ? (100 / prev24HoursLeads) * last24HoursLeads - 100
      : "-";

  const last7DaysLeadsChange =
    prev7DaysLeads > 0 ? (100 / prev7DaysLeads) * last7DaysLeads - 100 : "-";

  const last24HoursVerifiedLeadsChange =
    prev24HoursVerifiedLeads > 0
      ? (100 / prev24HoursVerifiedLeads) * prev24HoursVerifiedLeads - 100
      : "-";

  const last7DaysVerifiedLeadsChange =
    prev7DaysVerifiedLeads > 0
      ? (100 / prev7DaysVerifiedLeads) * last7DaysVerifiedLeads - 100
      : "-";

  const last24hoursVerifiedPercent =
    last24HoursLeads > 0
      ? Math.round((100 / last24HoursLeads) * last24HoursVerifiedLeads * 10) /
        10
      : 0;

  const last7DaysVerifiedPercent =
    last7DaysLeads > 0
      ? Math.round((100 / last7DaysLeads) * last7DaysVerifiedLeads * 10) / 10
      : 0;

  const allVerifiedPercent =
    allLeads > 0
      ? Math.round((100 / allLeads) * allVerifiedLeads * 10) / 10
      : 0;

  const listsList = data.listBuildingData.lists.map((list) => {
    const { id, name, last24hoursLeads, last7DaysLeads, allLeads } = list;

    return (
      <tr key={id}>
        <Cell>{name}</Cell>
        <Cell>{last24hoursLeads}</Cell>
        <Cell>{last7DaysLeads}</Cell>
        <Cell>{allLeads}</Cell>
        <Cell>
          <ButtonPrimarySmall
            onClick={() => {
              navigate(`/mailing-list?id=${id}&from=marketing-tools`);
            }}
          >
            Open List
          </ButtonPrimarySmall>
        </Cell>
      </tr>
    );
  });

  // prepare chart
  const reversedChartsData = JSON.parse(trendsData).reverse();

  let values = [];

  for (const value of reversedChartsData) {
    values.push(value.leads);
    values.push(value.verifiedLeads);
  }

  const yOneMin = Math.min(...values);
  const yOneMax = Math.max(...values);
  return (
    <ListBuilding>
      <TopContent>
        <Heading>
          <BoxTitle>
            <TitleIcon>
              <img src={emailsIcon} style={{ width: "100%" }} />
            </TitleIcon>
            Email Marketing List Building
          </BoxTitle>
          <Text>
            Create high-converting landing pages and build your email list. Our
            professionally designed pages will help you turn visitors into
            customers. It's easy - you can start collecting leads in just 3
            minutes.
          </Text>
        </Heading>
      </TopContent>
      <LeadsStats>
        <Stats>
          <SmallTitle>Email Leads Stats</SmallTitle>
          <StatsBox>
            <BoxLabel></BoxLabel>
            <BoxLabel>24 Hours</BoxLabel>
            <BoxLabel>7 Days</BoxLabel>
            <BoxLabel>Total</BoxLabel>
            <LabelIconCell>
              <LabelIcon>
                <img src={userCircleIcon} style={{ width: "100%" }} />
              </LabelIcon>
            </LabelIconCell>
            <ValueCell>
              {last24HoursLeads}
              {changeBadge(last24HoursLeadsChange)}
            </ValueCell>
            <ValueCell>
              {last7DaysLeads}
              {changeBadge(last7DaysLeadsChange)}
            </ValueCell>
            <ValueCell>{allLeads}</ValueCell>
            <LabelIconCell>
              <LabelIcon>
                <img src={userCircleWhiteIcon} style={{ width: "100%" }} />
              </LabelIcon>
            </LabelIconCell>
            <ValueCell>
              {last24HoursVerifiedLeads}
              {changeBadge(last24HoursVerifiedLeadsChange)}
            </ValueCell>
            <ValueCell>
              {last7DaysVerifiedLeads}
              {changeBadge(last7DaysVerifiedLeadsChange)}
            </ValueCell>
            <ValueCell>{allVerifiedLeads}</ValueCell>
            <LabelIconCell>
              <LabelIcon>
                <img src={percentWhiteIcon} style={{ width: "100%" }} />
              </LabelIcon>
            </LabelIconCell>
            <ValueCell>{last24hoursVerifiedPercent}%</ValueCell>
            <ValueCell>{last7DaysVerifiedPercent}%</ValueCell>
            <ValueCell>{allVerifiedPercent}%</ValueCell>
          </StatsBox>
        </Stats>
        <Trends>
          <TrendsHeader>
            <SmallTitle>Trends</SmallTitle>
            <TrendsLegend>
              <TrendsLegendItem>
                <TrendsLegendItemIcon>
                  <img src={dotIndigoIcon} style={{ width: "100%" }} />
                </TrendsLegendItemIcon>
                <TrendsLegendItemName>All leads</TrendsLegendItemName>
              </TrendsLegendItem>
              <TrendsLegendItem>
                <TrendsLegendItemIcon>
                  <img src={dotYellowIcon} style={{ width: "100%" }} />
                </TrendsLegendItemIcon>
                <TrendsLegendItemName>Verified leads</TrendsLegendItemName>
              </TrendsLegendItem>
            </TrendsLegend>
          </TrendsHeader>
          <TrendsBox>
            <LineChart
              width={238}
              height={96}
              margin={{ top: 5, bottom: 5, left: 5, right: 0 }}
              xAxis={[
                {
                  scaleType: "point",
                  dataKey: "name",
                },
              ]}
              yAxis={[{ id: "leads", min: yOneMin, max: yOneMax * 1.1 }]}
              grid={{ vertical: true }}
              series={[
                {
                  id: "leads",
                  dataKey: "leads",
                  label: "Leads",
                  color: "#8783D1",
                  yAxisId: "leads",
                },
                {
                  id: "verifiedLeads",
                  dataKey: "verifiedLeads",
                  label: "Verified Leads",
                  color: "#ffe70d",
                  yAxisId: "leads",
                },
              ]}
              dataset={reversedChartsData}
              slotProps={{
                legend: { hidden: true },
                popper: {
                  sx: {
                    boxShadow: 3,
                    border: "0px",
                    borderRadius: "15px",
                    backgroundColor: "#131F2A",
                    "& .MuiChartsTooltip-table": {
                      backgroundColor: "#131F2A",
                    },
                    "& .MuiTypography-root": {
                      color: "#fff",
                      fontSize: "13px",
                    },
                  },
                },
              }}
              sx={{
                "& .MuiChartsAxis-line": {
                  opacity: 0,
                },
                "& .MuiChartsAxis-tickContainer": {
                  opacity: 0,
                },
                "& .MuiChartsAxisHighlight-root": {
                  stroke: "#ffffff",
                },
                "& .MuiLineElement-series-leads": {
                  strokeWidth: 2,
                },
                "& .MuiMarkElement-series-leads": {
                  scale: "0",
                  strokeWidth: 0,
                },
                "& .MuiLineElement-series-verifiedLeads": {
                  strokeWidth: 2,
                },
                "& .MuiMarkElement-series-verifiedLeads": {
                  scale: "0",
                  strokeWidth: 0,
                },
                "& .MuiChartsGrid-verticalLine": {
                  stroke: "#757575",
                  strokeDasharray: "2 4",
                },
              }}
            />
          </TrendsBox>
        </Trends>
      </LeadsStats>
      <LandingPages>
        <SmallTitleWithTip>
          <SmallTitle>Landing Pages</SmallTitle>
          <Tip
            tipId="ranking"
            tip="Lead capture landing pages allow you to easily collect contact information from your audience and traffic.<br/><br/><a href='https://help.scalelup.com/en/articles/11883831-what-are-scalelup-landing-pages' target='_blank'  class='tooltip-link'>Learn more</a>"
            clickable
          />
        </SmallTitleWithTip>
        <Text>
          Click the button below to create and manage your landing pages.
        </Text>
        <ButtonWrap>
          <ButtonPrimarySmall onClick={() => navigate("/landing-pages")}>
            Open Landing Pages
          </ButtonPrimarySmall>
        </ButtonWrap>
      </LandingPages>
      <EmailLists>
        <SmallTitleWithTip>
          <SmallTitle>Mailing Lists</SmallTitle>
          <Tip
            tipId="ranking"
            tip="A mailing list is a collection of leads who have submitted their details using your landing pages.<br/><br/><a href='https://help.scalelup.com/en/articles/11884040-understanding-mailing-lists-and-lead-management' target='_blank'  class='tooltip-link'>Learn more</a>"
            clickable
          />
        </SmallTitleWithTip>
        {listsList.length > 0 && (
          <EmailListsBox>
            <TableWrap className="table-wrap">
              <ListTable>
                <TableHead>
                  <tr>
                    <TableLabel width="160px">Name</TableLabel>
                    <TableLabel width="130px">
                      <LabelWithIcon>
                        <LabelWithIconIcon>
                          <img src={userCircleIcon} style={{ width: "100%" }} />
                        </LabelWithIconIcon>
                        <LabelWithIconText>24 Hours</LabelWithIconText>
                      </LabelWithIcon>
                    </TableLabel>
                    <TableLabel width="100px">
                      <LabelWithIcon>
                        <LabelWithIconIcon>
                          <img src={userCircleIcon} style={{ width: "100%" }} />
                        </LabelWithIconIcon>
                        <LabelWithIconText>7 Days</LabelWithIconText>
                      </LabelWithIcon>
                    </TableLabel>
                    <TableLabel width="90px">
                      <LabelWithIcon>
                        <LabelWithIconIcon>
                          <img src={userCircleIcon} style={{ width: "100%" }} />
                        </LabelWithIconIcon>
                        <LabelWithIconText>Total</LabelWithIconText>
                      </LabelWithIcon>
                    </TableLabel>
                    <TableLabel width="100px"></TableLabel>
                  </tr>
                </TableHead>
                <TableBody>{listsList}</TableBody>
              </ListTable>
            </TableWrap>
          </EmailListsBox>
        )}
        {listsList.length === 0 && (
          <Text>You don't have any Mailing List.</Text>
        )}
        <ButtonWrap>
          <ButtonPrimarySmall
            onClick={() => {
              navigate("/mailing-lists");
            }}
          >
            Mailing Lists
          </ButtonPrimarySmall>
        </ButtonWrap>
      </EmailLists>
      <Autoresponders>
        <SmallTitleWithTip>
          <SmallTitle>Autoresponders</SmallTitle>
          <Tip
            tipId="autoresponders"
            tip="When you connect the autoresponder to your mailing list, we will automatically send the emails you have created to your leads on your behalf.<br><br>This will help you save time, provide instant communication, and ensure engagement with your leads."
            clickable
          />
        </SmallTitleWithTip>
        <Text>
          Use the autoresponder to automatically send a series of emails to your
          leads.
        </Text>
        <ButtonWrap>
          <ButtonPrimarySmall onClick={() => navigate("/autoresponders")}>
            Manage Autoresponders
          </ButtonPrimarySmall>
        </ButtonWrap>
      </Autoresponders>
    </ListBuilding>
  );
};
